import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2, Lock, Phone, User, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api, { authAPI } from '../services/api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import { initiateAmosIdLogin, isAmosIdAvailable } from '../utils/amosId';

// Helper to safely extract error message from API response
const getErrorMessage = (error, defaultMsg = 'Error desconocido') => {
  const detail = error?.response?.data?.detail || error?.data?.detail || error?.detail;
  if (!detail) return error?.message || defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // Pydantic validation errors are arrays of objects
    return detail[0]?.msg || defaultMsg;
  }
  return defaultMsg;
};

const STEPS = {
  LOGIN: 'login',
  NEW_PLAYER_START: 'new-player-start',
  NEW_PLAYER_VERIFY: 'new-player-verify',
  COMPLETE_PROFILE: 'complete-profile',
  RECOVER_START: 'recover-start',
  RECOVER_VERIFY: 'recover-verify',
};

const normalizePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? `+${digits}` : '';
};

const COUNTRY_CODES = [
  { value: '+52', label: '🇲🇽 +52' },
  { value: '+1', label: '🇺🇸 +1' },
  { value: '+54', label: '🇦🇷 +54' },
  { value: '+57', label: '🇨🇴 +57' },
  { value: '+34', label: '🇪🇸 +34' },
  { value: '+51', label: '🇵🇪 +51' },
  { value: '+56', label: '🇨🇱 +56' },
];

export default function Auth() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuthStore();

  const [step, setStep] = useState(STEPS.LOGIN);
  const [loading, setLoading] = useState(false);
  const [amosIdEnabled, setAmosIdEnabled] = useState(false);
  const [amosIdLoading, setAmosIdLoading] = useState(false);

  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [password, setPassword] = useState('');

  const [setupToken, setSetupToken] = useState('');
  const [firstName, setFirstName] = useState('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const role = user.role || 'player';
      if (role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (role === 'club_owner') {
        navigate('/club', { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Check if Amos ID is available on mount
  useEffect(() => {
    isAmosIdAvailable().then(setAmosIdEnabled).catch(() => setAmosIdEnabled(false));
  }, []);

  const handleAmosIdLogin = async () => {
    setAmosIdLoading(true);
    try {
      await initiateAmosIdLogin({ returnUrl: '/home' });
      // Will redirect to Amos ID
    } catch (err) {
      toast.error(err.message || 'No se pudo iniciar sesión con Amos ID');
      setAmosIdLoading(false);
    }
  };

  const buildPhoneWithCountryCode = () => {
    const raw = phone.trim();
    if (!raw) return '';

    if (raw.startsWith('+')) {
      return normalizePhone(raw);
    }

    const localDigits = raw.replace(/\D/g, '');
    const countryDigits = countryCode.replace(/\D/g, '');
    return localDigits ? `+${countryDigits}${localDigits}` : '';
  };

  const [lastName, setLastName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [recoveryCode, setRecoveryCode] = useState('');

  const [registrationCode, setRegistrationCode] = useState('');
  const [profileOrigin, setProfileOrigin] = useState('returning');

  const handleLogin = async () => {
    const identifier = buildPhoneWithCountryCode();

    if (!identifier) {
      toast.error('Ingresa tu celular');
      return;
    }
    if (!password) {
      toast.error('Ingresa tu contraseña');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login({
        identifier,
        password,
      });

      login(response.data.user, response.data.token);
      toast.success('Sesión iniciada');
      navigate('/home');
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};

      if (status === 428 && data.requires_profile_completion) {
        setSetupToken(data.setup_token || '');
        setFirstName(data.user?.first_name || '');
        setLastName(data.user?.last_name || '');
        setProfileOrigin('returning');
        setStep(STEPS.COMPLETE_PROFILE);
        toast.info('Completa nombre, apellido y contraseña para continuar');
      } else {
        toast.error(getErrorMessage({detail: data?.detail}, 'No se pudo iniciar sesión'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProfile = async () => {
    if (!setupToken) {
      toast.error('Tu sesión de configuración expiró. Inicia nuevamente');
      setStep(STEPS.LOGIN);
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Nombre y apellido son obligatorios');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await api.post(
        '/auth/complete-profile',
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          password: newPassword,
          confirm_password: confirmPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${setupToken}`,
          },
        }
      );

      const normalizedPhone = buildPhoneWithCountryCode();
      const loginResponse = await authAPI.login({
        identifier: normalizedPhone,
        password: newPassword,
      });

      login(loginResponse.data.user, loginResponse.data.token);
      toast.success('Perfil completado');

      if (profileOrigin === 'new') {
        navigate('/scan');
      } else {
        navigate('/home');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo completar el perfil'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartRegistration = async () => {
    const normalizedPhone = buildPhoneWithCountryCode();
    if (!normalizedPhone) {
      toast.error('Ingresa tu celular');
      return;
    }

    setLoading(true);
    try {
      await authAPI.startPlayerOTP(normalizedPhone);
      setRegistrationCode('');
      setStep(STEPS.NEW_PLAYER_VERIFY);
      toast.success('OTP enviado por SMS');
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo enviar OTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegistrationOTP = async () => {
    const normalizedPhone = buildPhoneWithCountryCode();
    if (!normalizedPhone) {
      toast.error('Ingresa tu celular');
      return;
    }
    if (registrationCode.length !== 6) {
      toast.error('Ingresa el OTP de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.verifyPlayerOTP(normalizedPhone, registrationCode);
      setSetupToken(response.data.token);
      setFirstName('');
      setLastName('');
      setNewPassword('');
      setConfirmPassword('');
      setProfileOrigin('new');
      setStep(STEPS.COMPLETE_PROFILE);
      toast.info('Completa tu nombre y contraseña para terminar tu cuenta');
    } catch (err) {
      toast.error(getErrorMessage(err, 'OTP inválido o expirado'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartRecovery = async () => {
    const normalizedPhone = buildPhoneWithCountryCode();
    if (!normalizedPhone) {
      toast.error('Ingresa tu celular con código de país');
      return;
    }

    setLoading(true);
    try {
      await authAPI.sendPasswordRecoveryOTP(normalizedPhone);
      toast.success('Si el número existe, recibirás un OTP por SMS');
      setStep(STEPS.RECOVER_VERIFY);
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo enviar el OTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRecovery = async () => {
    const normalizedPhone = buildPhoneWithCountryCode();
    if (!normalizedPhone) {
      toast.error('Ingresa tu celular con código de país');
      return;
    }
    if (recoveryCode.length !== 6) {
      toast.error('Ingresa el OTP de 6 dígitos');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await authAPI.confirmPasswordRecoveryOTP({
        phone: normalizedPhone,
        code: recoveryCode,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      toast.success('Contraseña actualizada. Ahora inicia sesión');
      setPassword(newPassword);
      setStep(STEPS.LOGIN);
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo recuperar la contraseña'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="auth-back-home-link">
          <ArrowLeft className="h-4 w-4" />
          Volver al inicio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-border bg-card p-6 shadow-lg"
          data-testid="auth-card"
        >
          <div className="mb-6 space-y-2 text-left">
            <h1 className="text-4xl font-bold">Clipper</h1>
            <p className="text-sm text-muted-foreground">Sports Replay App</p>
          </div>

          {step === STEPS.LOGIN && (
            <div className="space-y-4" data-testid="auth-login-form">
              <div className="space-y-2">
                <Label htmlFor="phone">Celular</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-32" data-testid="auth-login-country-code-select">
                      <SelectValue placeholder="+52" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="644 000 0000"
                      className="pl-10"
                      data-testid="auth-login-phone-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground" data-testid="auth-login-player-focus-text">
                  Inicia sesión con tu celular y contraseña.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    className="pl-10"
                    data-testid="auth-login-password-input"
                  />
                </div>
              </div>

              <Button onClick={handleLogin} disabled={loading} className="h-11 w-full" data-testid="auth-login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Iniciar sesión'}
              </Button>

              {/* Amos ID Login Button */}
              {amosIdEnabled && (
                <>
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">o continúa con</span>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={handleAmosIdLogin}
                    disabled={amosIdLoading}
                    className="h-11 w-full gap-2 border-emerald-600/30 bg-emerald-950/20 text-emerald-400 hover:bg-emerald-950/40 hover:text-emerald-300"
                    data-testid="auth-amos-id-login-button"
                  >
                    {amosIdLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Shield className="h-4 w-4" />
                        Iniciar con Amos ID
                      </>
                    )}
                  </Button>
                </>
              )}

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => setStep(STEPS.RECOVER_START)}
                  className="text-primary underline-offset-2 hover:underline"
                  data-testid="auth-forgot-password-button"
                >
                  Recuperar contraseña
                </button>
                <button
                  type="button"
                  onClick={() => setStep(STEPS.NEW_PLAYER_START)}
                  className="text-primary underline-offset-2 hover:underline"
                  data-testid="auth-create-account-button"
                >
                  Crear cuenta
                </button>
              </div>

              <Link to="/scan" className="block text-center text-xs text-muted-foreground underline-offset-2 hover:underline" data-testid="auth-go-scan-link">
                ¿Ya estás en la cancha? Escanea QR
              </Link>
            </div>
          )}


          {step === STEPS.NEW_PLAYER_START && (
            <div className="space-y-4" data-testid="auth-new-player-start-form">
              <p className="text-sm text-muted-foreground">Verifica tu celular para comenzar a grabar tus mejores jugadas.</p>

              <div className="space-y-2">
                <Label htmlFor="newPlayerPhone">Celular</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-32" data-testid="auth-new-player-country-code-select">
                      <SelectValue placeholder="+52" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="newPlayerPhone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="644 000 0000"
                    data-testid="auth-new-player-phone-input"
                  />
                </div>
              </div>

              <Button onClick={handleStartRegistration} disabled={loading} className="h-11 w-full" data-testid="auth-new-player-send-otp-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Obtener código'}
              </Button>

              <Button variant="ghost" onClick={() => setStep(STEPS.LOGIN)} className="w-full" data-testid="auth-new-player-back-login-button">
                Volver a login
              </Button>
            </div>
          )}

          {step === STEPS.NEW_PLAYER_VERIFY && (
            <div className="space-y-4" data-testid="auth-new-player-verify-form">
              <p className="text-sm text-muted-foreground">Ingresa el OTP para activar tu usuario.</p>

              <div className="space-y-2">
                <Label>OTP (6 dígitos)</Label>
                <InputOTP maxLength={6} value={registrationCode} onChange={setRegistrationCode} data-testid="auth-new-player-otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button onClick={handleVerifyRegistrationOTP} disabled={loading} className="h-11 w-full" data-testid="auth-new-player-verify-otp-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verificar OTP'}
              </Button>

              <Button variant="ghost" onClick={() => setStep(STEPS.NEW_PLAYER_START)} className="w-full" data-testid="auth-new-player-change-phone-button">
                Cambiar celular
              </Button>
            </div>
          )}

          {step === STEPS.COMPLETE_PROFILE && (
            <div className="space-y-4" data-testid="auth-complete-profile-form">
              <p className="text-sm text-muted-foreground">
                {profileOrigin === 'new'
                  ? 'Último paso: define tu nombre, apellido y contraseña para terminar tu cuenta.'
                  : 'Antes de tu segundo acceso, define nombre, apellido y contraseña.'}
              </p>

              <div className="space-y-2">
                <Label htmlFor="firstName">Nombre *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input 
                    id="firstName" 
                    value={firstName} 
                    onChange={(e) => setFirstName(e.target.value)} 
                    className="pl-10" 
                    placeholder="Tu nombre"
                    data-testid="auth-complete-firstname-input" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Apellido *</Label>
                <Input 
                  id="lastName" 
                  value={lastName} 
                  onChange={(e) => setLastName(e.target.value)} 
                  placeholder="Tu apellido"
                  data-testid="auth-complete-lastname-input" 
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Contraseña *</Label>
                <Input 
                  id="newPassword" 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  placeholder="Mínimo 8 caracteres"
                  data-testid="auth-complete-password-input" 
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar contraseña *</Label>
                <Input 
                  id="confirmPassword" 
                  type="password" 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  placeholder="Repite tu contraseña"
                  data-testid="auth-complete-confirm-password-input" 
                />
              </div>

              {/* Storage tip */}
              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-3 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>💡 Tip:</strong> Tus clips duran <strong>24 horas</strong>. 
                  Verifica tu email después para extenderlos a <strong>3 días</strong>.
                </p>
              </div>

              <Button onClick={handleCompleteProfile} disabled={loading} className="h-11 w-full" data-testid="auth-complete-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar y continuar'}
              </Button>
            </div>
          )}

          {step === STEPS.RECOVER_START && (
            <div className="space-y-4" data-testid="auth-recovery-start-form">
              <p className="text-sm text-muted-foreground">Recupera tu cuenta con OTP por SMS.</p>
              <div className="space-y-2">
                <Label htmlFor="recoverPhone">Celular</Label>
                <div className="flex gap-2">
                  <Select value={countryCode} onValueChange={setCountryCode}>
                    <SelectTrigger className="w-32" data-testid="auth-recovery-country-code-select">
                      <SelectValue placeholder="+52" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_CODES.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="recoverPhone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="644 000 0000"
                    data-testid="auth-recovery-phone-input"
                  />
                </div>
              </div>

              <Button onClick={handleStartRecovery} disabled={loading} className="h-11 w-full" data-testid="auth-recovery-send-otp-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enviar OTP'}
              </Button>

              <Button variant="ghost" onClick={() => setStep(STEPS.LOGIN)} className="w-full" data-testid="auth-recovery-back-login-button">
                Volver a login
              </Button>
            </div>
          )}

          {step === STEPS.RECOVER_VERIFY && (
            <div className="space-y-4" data-testid="auth-recovery-verify-form">
              <p className="text-sm text-muted-foreground">Ingresa OTP y define nueva contraseña.</p>

              <div className="space-y-2">
                <Label>OTP (6 dígitos)</Label>
                <InputOTP maxLength={6} value={recoveryCode} onChange={setRecoveryCode} data-testid="auth-recovery-otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recoveryPassword">Nueva contraseña</Label>
                <Input id="recoveryPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="auth-recovery-new-password-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="recoveryConfirmPassword">Confirmar contraseña</Label>
                <Input id="recoveryConfirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="auth-recovery-confirm-password-input" />
              </div>

              <Button onClick={handleConfirmRecovery} disabled={loading} className="h-11 w-full" data-testid="auth-recovery-confirm-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Actualizar contraseña'}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
